// ─────────────────────────────────────────────────────────────────────────────
// AuthProvider — global auth state (moved from src/context/AuthContext.js).
//
// Split into two contexts so components that only dispatch (Login) don't
// re-render when user/loading change, and consumers that only read state
// (Sidebar/Settings/Users) don't re-render when action identities change.
// Both context values are memoised. `useAuth()` is kept as a compatibility
// hook (state + actions) for consumers not yet migrated to the split hooks.
//
// Behaviour is identical to the previous AuthContext: seed user synchronously
// from storage, handle the global "auth:expired" event, login/MFA/logout.
// ─────────────────────────────────────────────────────────────────────────────
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import authService from "../../../services/authService";
import { TokenStorage } from "../../../services/api";

const AuthStateContext = createContext(null);
const AuthActionsContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => TokenStorage.getUser());
  const [loading, setLoading] = useState(false);
  const [mfaTempToken, setMfaTempToken] = useState(null);
  const navigate = useNavigate();

  // Global session-expiry handler (api.js dispatches "auth:expired" on 401).
  useEffect(() => {
    const handleExpiry = () => {
      TokenStorage.clear();
      setUser(null);
      setMfaTempToken(null);
      navigate("/login", { replace: true });
    };
    window.addEventListener("auth:expired", handleExpiry);
    return () => window.removeEventListener("auth:expired", handleExpiry);
  }, [navigate]);

  // Load the full user profile — including the `permissions` array RBAC UI needs.
  // Login/MFA payloads only carry `role`, so we enrich from /auth/me and cache it.
  const hydrateSession = useCallback(async () => {
    try {
      const me = await authService.me();
      if (me) { setUser(me); TokenStorage.setUser(me); }
      return me;
    } catch {
      return null; // offline / transient — keep the cached user; api.js handles 401.
    }
  }, []);

  // On load, if we already hold a token, refresh permissions so gating is current
  // (and self-heals sessions cached before permissions were tracked).
  useEffect(() => {
    if (TokenStorage.get()) hydrateSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const data = await authService.login(credentials);
      if (data?.mfa_required) {
        setMfaTempToken(data.temp_token);
        return { success: true, mfa_required: true, expires_in: data.expires_in };
      }
      // Enrich with permissions before entering the app so gating is correct on
      // the first render (login payload carries only `role`).
      const fullUser = await hydrateSession();
      if (!fullUser) setUser(data?.user ?? TokenStorage.getUser());
      navigate("/");
      return { success: true };
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [navigate, hydrateSession]);

  const verifyMfa = useCallback(async (code) => {
    if (!mfaTempToken) return { success: false, message: "No MFA session active" };
    setLoading(true);
    try {
      const data = await authService.verifyMfa({ temp_token: mfaTempToken, code });
      const fullUser = await hydrateSession();
      if (!fullUser) setUser(data?.user ?? TokenStorage.getUser());
      setMfaTempToken(null);
      navigate("/");
      return { success: true };
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [mfaTempToken, navigate, hydrateSession]);

  // Re-emails a fresh code for the current MFA challenge and stores the new
  // temp token (backend resets the 5-min window). Keeps mfaRequired true.
  const resendMfa = useCallback(async () => {
    if (!mfaTempToken) return { success: false, message: "No MFA session active" };
    setLoading(true);
    try {
      const data = await authService.resendMfa({ temp_token: mfaTempToken });
      if (data?.temp_token) setMfaTempToken(data.temp_token);
      return { success: true, expires_in: data?.expires_in };
    } catch (err) {
      return { success: false, message: err.message };
    } finally {
      setLoading(false);
    }
  }, [mfaTempToken]);

  // Abandon an in-flight MFA challenge (e.g. "Back to sign in").
  const clearMfa = useCallback(() => setMfaTempToken(null), []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setMfaTempToken(null);
    navigate("/login");
  }, [navigate]);

  const actions = useMemo(
    () => ({ login, verifyMfa, resendMfa, clearMfa, logout }),
    [login, verifyMfa, resendMfa, clearMfa, logout],
  );

  const state = useMemo(() => ({
    user,
    loading,
    mfaRequired: !!mfaTempToken,
    isAuthenticated: !!TokenStorage.get(),
  }), [user, loading, mfaTempToken]);

  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthStateContext.Provider value={state}>{children}</AuthStateContext.Provider>
    </AuthActionsContext.Provider>
  );
}

export function useAuthState() {
  const ctx = useContext(AuthStateContext);
  if (ctx === null) throw new Error("useAuthState must be used within an AuthProvider");
  return ctx;
}

export function useAuthActions() {
  const ctx = useContext(AuthActionsContext);
  if (ctx === null) throw new Error("useAuthActions must be used within an AuthProvider");
  return ctx;
}

// Compatibility hook — matches the old useAuth() shape (state + actions merged).
export function useAuth() {
  return { ...useAuthState(), ...useAuthActions() };
}
